import type { FC } from "react";
import styled from "styled-components";
import DateRangePicker from "../filters/DateRangePicker";
import MultiSelect from "../../atoms/inputs/MultiSelect";
import { useTranslation } from "react-i18next";
import RangeSelect from "../../atoms/inputs/RangeSelect";
import TextButton from "../../atoms/buttons/TextButton";
import { ClearIcon } from "../../atoms/icons/ClearIcon";
import { theme } from "../../../theme";
import Lookup from "../../organisms/Lookup";
import { VerticalSpacer } from "../../atoms/Spacers";
import type { InvoiceProcessStatus } from "../../../models/InvoiceStatusModel";

interface Props {
  invoiceDateFrom?: Date;
  invoiceDateTo?: Date;
  dueDateFrom?: Date;
  dueDateTo?: Date;
  onSelectDates: (
    dates: { from?: Date; to?: Date },
    type: "invoiceDate" | "dueDate",
  ) => void;
  statusOptions?: Array<{ value: string; label: string }>;
  selectedStatuses?: Array<string>;
  onSelectMultiselections?: (selectedOptions: any, type: "status") => void;
  onSelectAmount?: (minValue?: number, maxValue?: number) => void;
  selectedAmounts?: { min?: number; max?: number };
  onClearFilters: () => void;
  filterResetKey: string;
  setSelectedStatuses?: (
    statuses: Array<InvoiceProcessStatus> | undefined,
  ) => void;
  onSelectClient?: (item: any) => void;
  onClearClient?: () => void;
  selectedCompanyName?: string;
}

const FlexedDiv = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
`;

const InvoiceListFilterRow: FC<Props> = ({
  invoiceDateFrom,
  invoiceDateTo,
  dueDateFrom,
  dueDateTo,
  onSelectDates,
  statusOptions,
  selectedStatuses,
  onSelectMultiselections,
  onSelectAmount,
  selectedAmounts,
  onClearFilters,
  filterResetKey,
  setSelectedStatuses,
  onSelectClient,
  onClearClient,
  selectedCompanyName,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <FlexedDiv>
        {onSelectClient && (
          <>
            <Lookup key={`lookup-${filterResetKey}`} type="dropdownSmall" variant="light" onClickResult={onSelectClient} onClear={onClearClient} preselectedValue={selectedCompanyName} />
            <VerticalSpacer spacing={1} />
          </>
        )}
        <DateRangePicker
          key={`invoiceDate-${filterResetKey}`}
          from={invoiceDateFrom}
          to={invoiceDateTo}
          onSelect={({ from, to }) => {
            onSelectDates({ from, to }, "invoiceDate");
          }}
          titleKey="invoices.invoiceDates"
        />
        <DateRangePicker
          key={`dueDate-${filterResetKey}`}
          from={dueDateFrom}
          to={dueDateTo}
          onSelect={({ from, to }) => {
            onSelectDates({ from, to }, "dueDate");
          }}
          titleKey="invoices.dueDates"
        />
        {onSelectMultiselections && (
          <MultiSelect
            key={`status-${filterResetKey}`}
            options={statusOptions ?? []}
            selectedOptions={selectedStatuses ?? []}
            onChange={(selectedOptions) =>
              onSelectMultiselections(selectedOptions, "status")
            }
            onClickSelectAll={() => setSelectedStatuses?.(undefined)}
            title={t("common.status")}
          />
        )}
        {onSelectAmount && (
          <RangeSelect
            key={`amount-${filterResetKey}`}
            title={t("invoices.amount")}
            onChange={({ minValue, maxValue }) => {
              onSelectAmount(minValue, maxValue);
            }}
            selectedMax={selectedAmounts?.max}
            selectedMin={selectedAmounts?.min}
          />
        )}
        <VerticalSpacer spacing={0.5} />
        <TextButton
          icon={<ClearIcon color={theme.colors.textSecondary} />}
          title={t("invoices.clearFilters")}
          onClick={onClearFilters}
          color={theme.colors.textSecondary}
        />
      </FlexedDiv>
    </div>
  );
};

export default InvoiceListFilterRow;
